import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Subject, merge } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { WorkflowService } from '../../../../data-access/workflow.service';
import { AHRService } from 'app/domain';
import { WorkflowCaseIndex } from '../../../../domain/types/workflow';
import { getWorkflowDisplayName, formatYear, getCaseStatusClass } from '../workflow-utils';

const SORT_COLUMNS = ['candidateName', 'workflowType', 'statusSummary', 'periodOfReviewId', 'createdAt'] as const;
type SortColumn = typeof SORT_COLUMNS[number];
type SortDirection = 'asc' | 'desc';
type CompletionFilter = 'in-progress' | 'complete' | 'all';

@Component({
  selector: 'app-all-cases',
  templateUrl: './all-cases.component.html',
  styleUrls: ['./all-cases.component.scss']
})
export class AllCasesComponent implements OnInit, OnDestroy {
  allCases: WorkflowCaseIndex[] = [];
  loading = true;
  title = 'All Cases';
  emptyMessage = 'No cases found.';

  sortColumn: SortColumn = 'periodOfReviewId';
  sortDirection: SortDirection = 'desc';

  completionFilter: CompletionFilter = 'in-progress';

  private static readonly DEFAULT_SORT_COLUMN: SortColumn = 'periodOfReviewId';
  private static readonly DEFAULT_SORT_DIRECTION: SortDirection = 'desc';
  private static readonly DEFAULT_COMPLETION: CompletionFilter = 'in-progress';
  private destroy$ = new Subject<void>();

  candidateSearch = new FormControl<string>('');
  typeFilter = new FormControl<string[]>([]);
  statusFilter = new FormControl<string[]>([]);
  yearFilter = new FormControl<number[]>([]);

  distinctTypes: string[] = [];
  distinctStatuses: string[] = [];
  distinctYears: number[] = [];

  hasSearch = false;
  hasTypeFilter = false;
  hasStatusFilter = false;
  hasYearFilter = false;

  showCandidateSearch = true;
  private mode: 'all' | 'mine' = 'all';
  private currentProgramYear: number | null = null;

  getWorkflowDisplayName = getWorkflowDisplayName;
  formatYear = formatYear;
  getStatusClass = getCaseStatusClass;

  constructor(
    private workflowSvc: WorkflowService,
    private ahrSvc: AHRService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.candidateSearch.valueChanges.subscribe(v => this.hasSearch = !!v);
    this.typeFilter.valueChanges.subscribe(v => this.hasTypeFilter = !!(v && v.length));
    this.statusFilter.valueChanges.subscribe(v => this.hasStatusFilter = !!(v && v.length));
    this.yearFilter.valueChanges.subscribe(v => this.hasYearFilter = !!(v && v.length));
  }

  async ngOnInit() {
    this.mode = this.route.snapshot.data['mode'] || 'all';
    this.title = this.mode === 'mine' ? 'My Cases' : 'All Cases';
    this.emptyMessage = this.mode === 'mine' ? 'No cases assigned to you.' : 'No cases found.';
    this.showCandidateSearch = this.mode !== 'mine';

    this.hydrateFromQueryParams();

    // Authoritative current period of review (date-based, server-configured
    // cutoff, honors the simulated clock) — used to decide whether a selected
    // year is "previous". The endpoint returns the period-ending year; the year
    // filter keys on periodOfReviewId (= year + 1, per formatYear), so shift by 1.
    this.ahrSvc.getCurrentPeriodOfReviewYear().then(y => {
      const n = y ? +y : NaN;
      this.currentProgramYear = isNaN(n) ? null : n + 1;
    });

    // Selecting only previous program years nudges the completion filter to
    // Complete (those cases are almost always done, so the default In Progress
    // would show nothing). Fires only on user interaction — hydration uses
    // emitEvent:false.
    this.yearFilter.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(years => this.nudgeCompletionForPastYears(years || []));

    merge(
      this.candidateSearch.valueChanges.pipe(debounceTime(250)),
      this.typeFilter.valueChanges,
      this.statusFilter.valueChanges,
      this.yearFilter.valueChanges,
    ).pipe(takeUntil(this.destroy$)).subscribe(() => this.syncUrl());

    this.loading = true;
    try {
      this.allCases = this.mode === 'mine'
        ? await this.workflowSvc.getMyCases()
        : await this.workflowSvc.getAllCases();
      this.distinctTypes = [...new Set(this.allCases.map(c => c.workflowType))].sort();
      this.distinctStatuses = [...new Set(this.allCases.map(c => c.statusSummary))].sort();
      this.distinctYears = [...new Set(this.allCases.map(c => c.periodOfReviewId))].sort((a, b) => b - a);
    } catch (err) {
      console.error('Failed to load cases', err);
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hydrateFromQueryParams() {
    const qp = this.route.snapshot.queryParamMap;

    const done = qp.get('done');
    if (done === 'in-progress' || done === 'complete' || done === 'all') {
      this.completionFilter = done;
    }

    const sort = qp.get('sort');
    if (sort && (SORT_COLUMNS as readonly string[]).includes(sort)) this.sortColumn = sort as SortColumn;

    const dir = qp.get('dir');
    if (dir === 'asc' || dir === 'desc') this.sortDirection = dir;

    const name = qp.get('name');
    if (name) {
      this.candidateSearch.setValue(name, { emitEvent: false });
      this.hasSearch = true;
    }

    const types = qp.getAll('type');
    if (types.length) {
      this.typeFilter.setValue(types, { emitEvent: false });
      this.hasTypeFilter = true;
    }

    const statuses = qp.getAll('status');
    if (statuses.length) {
      this.statusFilter.setValue(statuses, { emitEvent: false });
      this.hasStatusFilter = true;
    }

    const years = qp.getAll('year').map(y => +y).filter(n => !isNaN(n));
    if (years.length) {
      this.yearFilter.setValue(years, { emitEvent: false });
      this.hasYearFilter = true;
    }
  }

  private syncUrl() {
    const queryParams: { [key: string]: string | string[] | number[] | null } = {
      name: this.candidateSearch.value || null,
      type: this.typeFilter.value?.length ? this.typeFilter.value : null,
      status: this.statusFilter.value?.length ? this.statusFilter.value : null,
      year: this.yearFilter.value?.length ? this.yearFilter.value : null,
      done: this.completionFilter === AllCasesComponent.DEFAULT_COMPLETION ? null : this.completionFilter,
      sort: this.sortColumn === AllCasesComponent.DEFAULT_SORT_COLUMN ? null : this.sortColumn,
      dir: this.sortDirection === AllCasesComponent.DEFAULT_SORT_DIRECTION ? null : this.sortDirection,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  get availableStatuses(): string[] {
    if (this.completionFilter === 'in-progress') {
      return this.distinctStatuses.filter(s => s !== 'Complete');
    }
    if (this.completionFilter === 'complete') {
      return [];
    }
    return this.distinctStatuses;
  }

  private nudgeCompletionForPastYears(years: number[]) {
    // Only nudge away from the default — respect an explicit Complete/All choice,
    // and don't act until we know the current year.
    if (this.completionFilter !== 'in-progress' || this.currentProgramYear == null) return;
    // A selection that includes the current year (or is cleared) is left alone.
    if (!years.length || years.includes(this.currentProgramYear)) return;
    this.setCompletionFilter('complete');
  }

  setCompletionFilter(value: CompletionFilter) {
    this.completionFilter = value;
    // Clear status selections that are no longer available
    const available = this.availableStatuses;
    const current = this.statusFilter.value || [];
    const valid = current.filter(s => available.includes(s));
    if (valid.length !== current.length) {
      this.statusFilter.setValue(valid, { emitEvent: false });
      this.hasStatusFilter = valid.length > 0;
    }
    this.syncUrl();
  }

  get filteredCases(): WorkflowCaseIndex[] {
    let cases = this.allCases;

    if (this.completionFilter === 'in-progress') {
      cases = cases.filter(c => c.status !== 'Complete');
    } else if (this.completionFilter === 'complete') {
      cases = cases.filter(c => c.status === 'Complete');
    }

    const search = (this.candidateSearch.value || '').trim().toLowerCase();
    if (search) {
      cases = cases.filter(c => c.candidateName.toLowerCase().includes(search));
    }

    const types = this.typeFilter.value;
    if (types && types.length > 0) {
      cases = cases.filter(c => types.includes(c.workflowType));
    }

    const statuses = this.statusFilter.value;
    if (statuses && statuses.length > 0) {
      cases = cases.filter(c => statuses.includes(c.statusSummary));
    }

    const years = this.yearFilter.value;
    if (years && years.length > 0) {
      cases = cases.filter(c => years.includes(c.periodOfReviewId));
    }

    return this.sortCases(cases);
  }

  toggleSort(column: SortColumn) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.syncUrl();
  }

  getSortIcon(column: SortColumn): string {
    if (this.sortColumn !== column) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  private sortCases(cases: WorkflowCaseIndex[]): WorkflowCaseIndex[] {
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return [...cases].sort((a, b) => {
      const valA = a[this.sortColumn];
      const valB = b[this.sortColumn];

      if (typeof valA === 'string' && typeof valB === 'string') {
        return dir * valA.localeCompare(valB);
      }
      if (typeof valA === 'number' && typeof valB === 'number') {
        return dir * (valA - valB);
      }
      return 0;
    });
  }

  viewPacket(c: WorkflowCaseIndex) {
    this.router.navigate(['/review/packet', c.caseId]);
  }
}
